import * as React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SurveyAnswerGraphData } from '../../../../../../../amorg-common';
import { Card } from '../../../../../../../designSystem/Card';
import { colors } from '../../../../../../../designSystem/colors/color';
import { toDateNoYearString } from '../../../../../../../utils/date';

type AnswerGraphProps = {
    data:SurveyAnswerGraphData
}
export function AnswerGraph(props:AnswerGraphProps):JSX.Element
{
    const ref = useRef<HTMLDivElement | null>(null);
    const [graphWidthPx, setGraphWidthPx] = useState(0);

    useLayoutEffect(() => {
        if(ref.current)
            setGraphWidthPx(ref.current.offsetWidth);
    }, []);


    useLayoutEffect(() => {
        function handleResize() {
            if(ref.current)
                setGraphWidthPx(ref.current.offsetWidth)
        }

        window.addEventListener('resize', handleResize)
    })

    return (
        <div ref={ref}>
            <AnswerGraphics data={props.data} graphWidthPx={graphWidthPx} graphHeightPx={200} />
        </div>
    );
}

type AnswerGraphicsProps = {
    data:SurveyAnswerGraphData,
    graphWidthPx:number,
    graphHeightPx:number
}
export function AnswerGraphics(props:AnswerGraphicsProps):JSX.Element
{
    const startedAnswerColor = colors.graphs.survey.score.score4;
    const finishedAnswerColor = colors.graphs.survey.score.score5;

    const counterTextHeight = 20;
    const axisHeight = 16;
    const legendHeight = 50;
    const colPairWidth = 80;
    const colWidth = colPairWidth / 2;
    const maxCount = 
        props.data 
        ? Math.max(...[...props.data.stats.map(s => s.totalStartedAnswers), ...props.data.stats.map(s => s.totalFinishedAnswers)])
        : 1;
    const countHeight = (props.graphHeightPx - counterTextHeight - axisHeight - legendHeight)/maxCount;
    return (
        <div>
            <Card variant="outlined">
                <GraphAreaDiv height={props.graphHeightPx-axisHeight-legendHeight}>
                    {props.data && props.data.stats.map((v) => {
                        return (
                            // Wraps two columns
                            <div key={v.date.toISOString()}>
                                <div style={{display:'flex', flexDirection:'row', alignItems:'flex-end', gap:2}}>
                                    <div>
                                        {v.totalStartedAnswers > 0 &&
                                            <CounterTextDiv height={counterTextHeight}>{v.totalStartedAnswers}</CounterTextDiv>
                                        }
                                        <CountBarDiv width={colWidth} height={v.totalStartedAnswers * countHeight} color={startedAnswerColor} />
                                    </div>
                                    <div>
                                        {v.totalFinishedAnswers > 0 &&
                                            <CounterTextDiv height={counterTextHeight}>{v.totalFinishedAnswers}</CounterTextDiv>
                                        }
                                        <CountBarDiv width={colWidth} height={v.totalFinishedAnswers * countHeight} color={finishedAnswerColor} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </GraphAreaDiv>
                <GraphAxisDiv height={axisHeight}>
                    <>
                        {props.data && props.data.stats.map((v) => {
                            return (
                                <DateTextDiv key={v.date.toISOString()}>
                                    {toDateNoYearString(v.date)}
                                </DateTextDiv>
                            );
                        })}
                    </>
                </GraphAxisDiv>
            </Card>

            <LegendDiv height={legendHeight} startedAnswerColor={startedAnswerColor} finishedAnswerColor={finishedAnswerColor}>
                <div />
                <span style={{fontSize:'14px'}}>Antal oprettede besvarelser</span>
                <div />
                <span style={{fontSize:'14px'}}>Antal gennemførte besvarelser</span>
            </LegendDiv>
        </div>
    );
}

const GraphAreaDiv = styled.div<{height:number}>`
    display:flex;
    flex-direction:row;
    height:${props => props.height}px;
    align-items:flex-end;
    justify-content:space-around;
`;

const GraphAxisDiv = styled.div<{height:number}>`
    border-top: 1px solid ${colors.divider.medium};
    height: ${props => props.height}px;
    display:flex;
    flex-direction:row;
    justify-content:space-around;
`;

const CounterTextDiv = styled.div<{height:number}>`
    height:${props => props.height}px;
    display:flex;
    justify-content:center;
    font-size: 0.75rem;
`;

const CountBarDiv = styled.div<{width:number, height:number, color:string}>`
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    width:${props => props.width}px;
    height:${props => props.height}px;
    background-color:${props => props.color};
`;

const DateTextDiv = styled.div`
    display:flex;
    justify-content:center;
    font-size: 0.75rem;
`;

const LegendDiv = styled.div<{height:number, startedAnswerColor:string, finishedAnswerColor:string}>`
    height:${props => props.height}px;
    display:flex; 
    align-items:center;
    & div {
        display:inline-block;
        width:20px; 
        height:20px; 
        margin-left:5px;
        margin-right:5px;
        border-radius: 4px;
    }
    & div:nth-child(1){
        margin-left:20px;
        background-color:${props => props.startedAnswerColor};
    }
    & div:nth-child(3){
        margin-left:20px;
        background-color:${props => props.finishedAnswerColor};
    }
    span {
        line-height: 20px;
    }
`;